import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../../shared/api/client";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../../shared/ui";
import type { ThroughputReport } from "../../../shared/types/api";
import { Figure, days } from "./reportBits";
import { FilterBar, defaultFilters, toQuery, type ReportFilterValues } from "./reportFilters";

/**
 * Who is delivering how much: completed and open counts, average cycle time, and hours logged.
 *
 * <p>Completed counts finishes inside the window; open is a snapshot as it stands. Cycle time is
 * the days from starting a task to finishing it, averaged over the finishes that recorded both
 * ends — so it is blank, not zero, for someone whose work never recorded a start.
 */
export function Throughput() {
  const [filters, setFilters] = useState<ReportFilterValues>(defaultFilters);

  const report = useQuery({
    queryKey: ["report", "throughput", filters],
    queryFn: () => api.get<ThroughputReport>(`/api/v1/reports/throughput?${toQuery(filters)}`),
  });

  return (
    <div className="space-y-4">
      <FilterBar values={filters} onChange={setFilters} />

      {report.isPending ? (
        <LoadingState label="Counting what shipped" />
      ) : report.isError ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : (
        <Report data={report.data} />
      )}
    </div>
  );
}

function Report({ data }: { data: ThroughputReport }) {
  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <Figure label="People" value={String(data.peopleCount)} note="with assigned work" />
        <Figure label="Completed" value={String(data.completedTotal)} note="finished in range" tone="green" />
        <Figure label="Still open" value={String(data.openTotal)} note="as it stands" />
      </div>

      <Card>
        <CardHeader title="By person" subtitle="Most delivered first." />
        <CardBody className="p-0">
          {data.people.length === 0 ? (
            <EmptyState title="Nobody to report on" message="No assigned work in this window and filter." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                    <th className="px-4 py-2.5 font-medium">Person</th>
                    <th className="px-4 py-2.5 text-right font-medium">Completed</th>
                    <th className="px-4 py-2.5 text-right font-medium">Open</th>
                    <th className="px-4 py-2.5 text-right font-medium">Overdue</th>
                    <th className="px-4 py-2.5 text-right font-medium">Avg cycle</th>
                    <th className="px-4 py-2.5 text-right font-medium">Logged</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {data.people.map((row) => (
                    <tr key={row.employeeId} className="hover:bg-ink-50">
                      <td className="px-4 py-3">
                        <span className="text-ink-900">{row.employeeName}</span>
                        {row.department && (
                          <span className="mt-0.5 block text-xs text-ink-500">{row.department}</span>
                        )}
                      </td>
                      <td className="tnum px-4 py-3 text-right text-emerald-700">
                        {row.completedCount}
                      </td>
                      <td className="tnum px-4 py-3 text-right text-ink-700">{row.openCount}</td>
                      <td className="tnum px-4 py-3 text-right">
                        {row.overdueOpenCount > 0 ? (
                          <span className="text-red-700">{row.overdueOpenCount}</span>
                        ) : (
                          <span className="text-ink-400">—</span>
                        )}
                      </td>
                      <td className="tnum px-4 py-3 text-right text-ink-700">
                        {days(row.averageCycleTimeDays)}
                      </td>
                      <td className="tnum px-4 py-3 text-right text-ink-900">
                        {Math.round(row.loggedHours * 10) / 10}h
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
